import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { CaraWorkspaceArtifactType, type CaraWorkspaceFieldsFragment } from '~/api/graphql';

type Artifact = CaraWorkspaceFieldsFragment['artifacts'][0];

/**
 * The artifact a `?artifact_id=&version=` link should open.
 *
 * Opens the version named in the link. A version that is gone opens nothing
 * rather than something else.
 */
const linkedArtifact = (artifacts: Artifact[], artifactId: string, version: number) =>
  artifacts.find((candidate) => candidate.id === artifactId && candidate.version === version);

/** The artifact a location's query string links to, if any. */
export const linkedArtifactId = (search: string) => new URLSearchParams(search).get('artifact_id');

/**
 * Translates URL query params into focus/convert actions:
 *
 *  - `?artifact_id=X&version=N` opens the artifact in the focus dialog, once
 *    per history entry.
 *  - `&convert_to_template=true` additionally kicks off the convert-to-template flow
 *    for DocumentHtml artifacts, then strips itself from the URL via replaceState so
 *    we don't re-trigger.
 *
 * This is the only hook in the workspace package that knows about routing.
 */
export function useUrlArtifactSync(args: {
  workspace: CaraWorkspaceFieldsFragment | undefined;
  openArtifactFocusDialogUnchecked: (artifact: Artifact) => void;
  startConvertFromArtifactId: (artifact: Artifact) => void;
  markUrlConvertProcessed: (key: string) => boolean;
}) {
  const {
    workspace,
    openArtifactFocusDialogUnchecked,
    startConvertFromArtifactId,
    markUrlConvertProcessed
  } = args;

  const location = useLocation();
  const navigate = useNavigate();
  const handledLocation = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!workspace?.artifacts) return;
    const locationKey = JSON.stringify([workspace.id, location.pathname, location.search]);
    if (handledLocation.current === locationKey) return;
    handledLocation.current = null;

    const params = new URLSearchParams(location.search);
    const artifactId = linkedArtifactId(location.search);
    const versionParam = params.get('version');
    const convertToTemplate = params.get('convert_to_template') === 'true';

    if (!artifactId || !versionParam || isNaN(parseInt(versionParam))) return;

    const version = parseInt(versionParam);
    const artifact = linkedArtifact(workspace.artifacts, artifactId, version);
    if (!artifact) return;

    // Later workspace ticks must not undo a version the reader or Cara selected.
    handledLocation.current = locationKey;
    openArtifactFocusDialogUnchecked(artifact);

    if (convertToTemplate && artifact.type === CaraWorkspaceArtifactType.DocumentHtml) {
      const key = `${artifactId}-${version}`;
      if (!markUrlConvertProcessed(key)) return;

      startConvertFromArtifactId(artifact);

      // Clean up the query parameter to avoid re-triggering on subsequent renders.
      const newParams = new URLSearchParams(location.search);
      newParams.delete('convert_to_template');
      const newSearch = newParams.toString();
      navigate(
        {
          pathname: location.pathname,
          search: newSearch ? `?${newSearch}` : ''
        },
        { replace: true }
      );
    }
  }, [
    location.search,
    location.pathname,
    workspace?.id,
    workspace?.artifacts,
    navigate,
    openArtifactFocusDialogUnchecked,
    startConvertFromArtifactId,
    markUrlConvertProcessed
  ]);
}
